/**
 * Creates a pollable function, with configurable interval, maximum attempt count and validation.
 * Defaults to 3000ms interval and a maximum of 10 attempts.
 * The poll's execution can be stopped by invoking the `stop` method: this is useful during component teardown, to avoid memory leaks.
 * The poll's internal state is reset after a stop, a completion or a rejection, in order to make it re-usable.
 * @param {() => any | () => Promise<any>} fn
 * @param {{interval: number, maxAttempts: number, validate: (value: any) => boolean}} options
 * @returns {{start(): Promise<any>, stop(): void}}
 */
export default function poll(fn, {interval = 3000, maxAttempts = 10, validate}) {
	let attempts = 0
	let stopped = false
	let timeoutId

	function resetInternalPollState() {
		clearTimeout(timeoutId)
		attempts = 0
		stopped = false
	}

	async function executePoll(resolve, reject) {
		if (stopped) {
			resetInternalPollState()
			return reject(new Error('Poll was stopped.'))
		}

		const response = await fn()
		const isValidResponse = await validate(response)

		if (isValidResponse) {
			resetInternalPollState()
			return resolve(response)
		}

		if (attempts >= maxAttempts) {
			resetInternalPollState()
			return reject(new Error('Maximum amount of attempts reached.'))
		}

		timeoutId = setTimeout(executePoll, interval, resolve, reject)
		attempts++
	}

	{
		return {
			start() {
				return new Promise(executePoll)
			},
			stop() {
				stopped = true
			},
		}
	}
}
